// src/render/imageInline.ts
// Robust inline image renderer that supports @@ ... @@ tokens even if a linkifier
// or other DOM manipulations split the token across multiple nodes. Also supports
// link: and href: attributes inside the token and safely wraps the image in an
// anchor when provided.

import { parseInlineAttrs } from "@/markdown/parseInlineAttrs";
import { getBaseUrl } from "@/utils/config";
import { applyAnimAttrs, seconds } from "@/utils/anim";
import { resolveImageSrc } from "@/utils/resolveAssets";

/* ---------------- helpers used by the token processor ---------------- */

function looksLikeEmail(u: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(u);
}
function looksLikePhone(u: string): boolean {
  // Permissive: digits with typical separators; we'll sanitize for tel:
  return /^\+?[0-9][0-9()\-\s.]{5,}$/.test(u);
}

/**
 * Normalize and validate an href. Rejects dangerous schemes, auto-handles
 * mailto/tel, resolves relative links against baseUrl, and sets a sensible default target.
 */
function normalizeHref(baseUrl: string, h?: string): { href: string; target?: "_blank" | "_self" } | null {
  if (!h) return null;
  const u = String(h).trim();
  // Disallow dangerous schemes.
  if (/^(javascript:|data:|vbscript:)/i.test(u)) return null;

  // Allow explicit safe schemes / hash / protocol-relative
  if (/^(mailto:|tel:|#)/i.test(u)) return { href: u, target: "_self" };
  if (/^https?:\/\//i.test(u)) return { href: u, target: "_blank" };
  if (/^\/\//.test(u)) return { href: u, target: "_blank" };

  // Bare email / phone → mailto:/tel:
  if (looksLikeEmail(u)) return { href: `mailto:${u}`, target: "_self" };
  if (looksLikePhone(u)) {
    const digits = u.replace(/[^\d+]/g, "");
    return { href: `tel:${digits}`, target: "_self" };
  }

  // Relative path → resolve against base
  const resolved = u.startsWith("/") ? u : `${baseUrl}/${u}`.replace(/\/{2,}/g, "/");
  return { href: resolved, target: "_self" };
}


type ImgSpec = {
  image: string;
  alt?: string;
  href?: string;
  target?: "_blank" | "_self" | "_parent" | "_top";
  class?: string;
  width?: number | string;
  height?: number | string;
  anim?: string;
  delay?: number | string;
  dur?: number | string;
  dist?: number | string;
};

function buildInlineImage(section: HTMLElement, attrs: ImgSpec, defaultDelay = 0): HTMLElement {
  const baseUrl = getBaseUrl();
  const src = resolveImageSrc(baseUrl, attrs.image || "");
  if (!src) {
    const span = document.createElement("span");
    span.textContent = attrs.image || "";
    return span;
  }

  const img = document.createElement("img");
  img.decoding = "async";
  (img as any).fetchPriority = "low";
  img.loading = "lazy";
  img.src = src;

  // alt
  if (Object.prototype.hasOwnProperty.call(attrs, "alt")) {
    img.alt = String(attrs.alt ?? "").replace(/^['"]|['"]$/g, "");
  } else {
    img.alt = src.split("/").pop()?.split(".")[0] || "image";
  }

  // Size
  if (attrs.width != null && `${attrs.width}`.trim() !== "") {
    const s = String(attrs.width).trim();
    if (/^\d+(\.\d+)?$/.test(s)) img.width = Math.max(1, Math.round(Number(s)));
    else img.style.width = s;
  }
  if (attrs.height != null && `${attrs.height}`.trim() !== "") {
    const s = String(attrs.height).trim();
    if (/^\d+(\.\d+)?$/.test(s)) img.height = Math.max(1, Math.round(Number(s)));
    else img.style.height = s;
  }

  // classes / perf hints
  img.classList.add("block", "transition-none");
  if (attrs.class) String(attrs.class).split(/\s+/).forEach((c) => {
    const clean = c.replace(/^['"]|['"]$/g, "");
    if (clean) img.classList.add(clean);
  });

  const delaySec = seconds(attrs.delay ?? defaultDelay, 0);
  const durSec = seconds(attrs.dur, undefined as any);
  const distVal = attrs.dist != null && `${attrs.dist}`.trim() !== "" ? String(attrs.dist).trim() : undefined;
  applyAnimAttrs(img, { anim: attrs.anim || "fade-in", delaySec, durSec, distPx: distVal ? Number(distVal) as any : undefined });

  // Optional anchor
  if (attrs.href) {
    const norm = normalizeHref(baseUrl, attrs.href);
    if (norm) {
      const a = document.createElement("a");
      a.href = norm.href;
      a.target = norm.target ?? "_blank";
      if (a.target === "_blank") a.rel = "noopener noreferrer";
      a.classList.add("cursor-pointer");
      a.setAttribute("data-anim-skip", "1"); // only IMG animates

      const aria = (img.alt && img.alt.trim() !== "") ? img.alt : (src.split(".")[0] || "image");
      a.setAttribute("aria-label", aria);

      // If author centers IMG with mx-auto, center the anchor too.
      try {
        const wantsCenter = attrs.class ? /\bmx-auto\b/.test(String(attrs.class)) : false;
        if (wantsCenter) {
          a.style.display = "block";
          (a.style as any).marginInline = "auto";
        }
      } catch {}

      a.appendChild(img);
      return a;
    }
  }
  return img;
}

/* ---------- Minimal, quote-aware fallback lexer ----------
   Why: parseInlineAttrs may miss `href`/`link` when `:` lives inside values (e.g., https://).
   This lexer parses key: value pairs safely; values may be quoted or bare.
*/
function safeLexInlineAttrs(input: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  let i = 0;

  const skipWs = () => { while (i < input.length && /\s/.test(input[i]!)) i++; };

  const readKey = () => {
    const start = i;
    while (i < input.length && /[A-Za-z0-9_\-]/.test(input[i]!)) i++;
    return input.slice(start, i).trim();
  };

  const readValue = (): string => {
    skipWs();
    const ch = input[i];
    if (ch === '"' || ch === "'") {
      const q = ch; i++;
      let s = "";
      while (i < input.length) {
        const c = input[i]!;
        if (c === "\\" && i + 1 < input.length) { s += input[i + 1]!; i += 2; continue; }
        if (c === q) { i++; break; }
        s += c; i++;
      }
      return s;
    }
    const start = i;
    let depth = 0;
    while (i < input.length) {
      const c = input[i]!;
      if (c === "(") depth++;
      if (c === ")") depth = Math.max(0, depth - 1);
      if (depth === 0 && c === ",") break;
      i++;
    }
    return input.slice(start, i).trim();
  };

  const coerce = (v: string): string | number | boolean | null => {
    const v0 = v.trim();
    const lower = v0.toLowerCase();
    if (lower === "true") return true;
    if (lower === "false") return false;
    if (lower === "null") return null;
    if (/^(https?:\/\/|\/\/|mailto:|tel:|#)/i.test(v0)) return v0;
    const t = v0.match(/^(-?\d+(?:\.\d+)?)(ms|s)$/i);
    if (t) return v0; // keep units
    const n = Number(v0);
    return Number.isFinite(n) ? n : v0;
  };

  while (i < input.length) {
    skipWs();
    if (i >= input.length) break;
    const key = readKey();
    skipWs();
    if (!key) {
      const nextComma = input.indexOf(",", i);
      i = nextComma === -1 ? input.length : nextComma + 1;
      continue;
    }
    if (input[i] === ":") i++;
    skipWs();
    const raw = readValue();
    skipWs();
    if (input[i] === ",") i++;
    out[key] = coerce(raw);
  }
  return out;
}

/**
 * Process a single @@ ... @@ token and return the generated element, or null on failure.
 */
function processAtAtImageTag(section: HTMLElement, spec: string, defaultDelay: number): HTMLElement | null {
  try {
    const inner = spec.slice(2, -2).trim();
    const hasBraces = inner.includes("{");
    let attrs: any = {} as ImgSpec;

    if (hasBraces) {
      const filename = inner.split("{")[0].trim();
      const rawBody = inner.slice(inner.indexOf("{") + 1).replace(/}\s*$/, "");
      const body = rawBody.replace(/^['"]|['"]$/g, "");

      // Try primary parser; if href/link clearly authored but missing, fallback.
      let parsed: Record<string, unknown> = {};
      let primaryFailed = false;
      try {
        parsed = parseInlineAttrs(body || "") || {};
      } catch {
        primaryFailed = true;
        parsed = {};
      }

      const needLinkish = /\b(href|link)\s*:/.test(body);
      const missingLinkish = needLinkish && (parsed as any).href == null && (parsed as any).link == null;
      const finalAttrs = (primaryFailed || missingLinkish) ? safeLexInlineAttrs(body) : parsed;

      // Allow 'link' as a safe alias for 'href'
      if ((finalAttrs as any).link && !(finalAttrs as any).href) {
        (finalAttrs as any).href = (finalAttrs as any).link;
      }

      (attrs as any) = { image: filename, ...finalAttrs };
    } else {
      (attrs as any).image = inner.replace(/^['"]|['"]$/g, "");
    }

    if (!attrs?.image) return null;
    return buildInlineImage(section, attrs, defaultDelay);
  } catch (e) {
    console.warn("[imageInline@@] failed:", spec, e);
    return null;
  }
}

/* --------------------------------------------------------------------------------------- */
/* Scans a section for @@ ... @@ tokens and replaces them with the generated element(s).   */
/* This version is resilient to tokens split across multiple text nodes by linkifiers.     */
/* --------------------------------------------------------------------------------------- */
export function scanAndRenderInlineImages(section: HTMLElement, defaultDelay = 0): void {
  // Quick bailout
  const hint = section.textContent || "";
  if (!hint || hint.indexOf("@@") === -1) return;

  // We use a loop that repeatedly searches for the next @@...@@ span with a Range,
  // because linkifiers may have split a single token across multiple nodes.
  // To avoid infinite loops on malformed content, cap total iterations.
  let safety = 200;

  // Helper: find next @@...@@ as a DOM Range starting from a given Node
  const findNextTokenRange = (): Range | null => {
    // 1) Find a text node that contains a starting "@@"
    const startIter = document.createNodeIterator(section, NodeFilter.SHOW_TEXT, {
      acceptNode: (node: Node) => {
        const t = (node as Text).nodeValue || "";
        return t.includes("@@") ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      }
    });

    let startNode: Text | null = null;
    let startOffset = 0;

    while ((startNode = startIter.nextNode() as Text | null)) {
      const t = startNode.nodeValue || "";
      const idx = t.indexOf("@@");
      if (idx !== -1) {
        startOffset = idx;
        break;
      }
    }
    if (!startNode) return null;

    // 2) Create a range starting at the opening "@@"
    const rng = document.createRange();
    rng.setStart(startNode, startOffset);

    // 3) Find the closing "@@" which might be in the same or a later text node
    //    First, check the same text node after the opening.
    const afterOpen = (startNode.nodeValue || "").slice(startOffset + 2);
    const closeHere = afterOpen.indexOf("@@");
    if (closeHere !== -1) {
      rng.setEnd(startNode, startOffset + 2 + closeHere + 2);
      return rng;
    }

    // Otherwise, walk forward through all nodes until we find a text node with "@@"
    const fwd = document.createTreeWalker(section, NodeFilter.SHOW_TEXT);
    fwd.currentNode = startNode;
    let endNode: Text | null = null;
    let endOffset = 0;

    while (true) {
      const n = fwd.nextNode() as Text | null;
      if (!n) break;
      const s = n.nodeValue || "";
      const pos = s.indexOf("@@");
      if (pos !== -1) {
        endNode = n; endOffset = pos + 2; // include closing '@@'
        break;
      }
    }

    if (!endNode) return null;
    rng.setEnd(endNode, endOffset);
    return rng;
  };

  // Main replacement loop
  while (safety-- > 0) {
    const tokenRange = findNextTokenRange();
    if (!tokenRange) break;

    // Extract plain text of the token, which collapses any injected tags (e.g., linkifier anchors)
    const frag = tokenRange.cloneContents();
    const tokenText = (frag.textContent || "").trim();

    if (!tokenText.startsWith("@@") || !tokenText.endsWith("@@")) {
      // If the fragment didn't contain a full token, remove the leading '@@' to avoid re-detection and continue.
      // But be conservative: if it clearly starts with '@@' but lacks a close, stop to avoid destructive edits.
      if (tokenText.startsWith("@@") && tokenText.indexOf("@@", 2) === -1) break;
      // Otherwise, advance by deleting the leading '@@' occurrence to prevent infinite loops.
      const tnode = tokenRange.startContainer as Text;
      if (tnode && tnode.nodeType === Node.TEXT_NODE) {
        const v = tnode.nodeValue || "";
        const idx = v.indexOf("@@");
        if (idx !== -1) {
          const tmp = document.createRange();
          tmp.setStart(tnode, idx);
          tmp.setEnd(tnode, idx + 2);
          tmp.deleteContents();
        }
      }
      continue;
    }

    const el = processAtAtImageTag(section, tokenText, defaultDelay);
    if (!el) {
      // Could not parse — remove the token markers to avoid an infinite loop and continue.
      // Replace only the leading '@@' to move forward.
      const tnode = tokenRange.startContainer as Text;
      if (tnode && tnode.nodeType === Node.TEXT_NODE) {
        const v = tnode.nodeValue || "";
        const idx = v.indexOf("@@");
        if (idx !== -1) {
          const tmp = document.createRange();
          tmp.setStart(tnode, idx);
          tmp.setEnd(tnode, idx + 2);
          tmp.deleteContents();
        }
      }
      continue;
    }

    // Successful parse — replace the whole token with the generated element.
    tokenRange.deleteContents();
    tokenRange.insertNode(el);
  }
}